import MotionController from "../lib/motion_controller"
import { gsap, ScrollTrigger, observeVisibility } from "../lib/motion"

// Sticky scrollytelling.
//
// mode "pin":   pins the element on desktop for `distance`% of a viewport (inner controllers
//               scrub their own timelines over the same range).
// mode "steps": pins on desktop and maps scroll progress to the active step. Copy (step
//               targets) and media (panel targets) crossfade; panels receive `demo:play`.
//               Below md there is no pin: panels auto-cycle while visible, copy stays stacked.
//
// Targets: pin + spacer (required: `spacer` must be the direct parent of `pin` in the markup.
// GSAP otherwise re-parents pinned elements into its own spacer on every refresh, which
// Stimulus sees as a disconnect/connect of every controller inside — an endless loop),
// step, panel, segment (per-step progress bars), progress (scaleY rail).
export default class extends MotionController {
  // Pins change the scroll length of everything below them, so they can't wait.
  static eager = true
  static targets = [ "spacer", "pin", "step", "panel", "segment", "progress" ]
  static values = {
    mode: { type: String, default: "steps" },
    distance: { type: Number, default: 100 },
    cycle: { type: Number, default: 7 }
  }

  animate({ desktop, mobile }) {
    if (this.modeValue === "pin") {
      if (desktop) this.pin()
      return
    }
    if (!desktop && !mobile) return
    return desktop ? this.stepsDesktop() : this.stepsMobile()
  }

  pin() {
    this.element.classList.add("is-pinned")
    ScrollTrigger.create({
      trigger: this.pinTarget,
      start: "top top",
      end: `+=${this.distanceValue}%`,
      pin: this.pinTarget,
      pinSpacer: this.spacerTarget,
      anticipatePin: 1
    })
    return () => this.element.classList.remove("is-pinned")
  }

  stepsDesktop() {
    const count = this.panelTargets.length
    this.element.classList.add("is-animated", "is-pinned")
    this.current = -1
    this.activate(0, { desktop: true, immediate: true })

    ScrollTrigger.create({
      trigger: this.pinTarget,
      start: "top 60%",
      onEnter: () => this.playPanel(this.current)
    })

    ScrollTrigger.create({
      trigger: this.pinTarget,
      start: "top top",
      end: `+=${this.distanceValue * count}%`,
      pin: this.pinTarget,
      pinSpacer: this.spacerTarget,
      anticipatePin: 1,
      onUpdate: (self) => {
        const index = Math.min(count - 1, Math.floor(self.progress * count))
        this.activate(index, { desktop: true })
        this.fill(self.progress * count)
        if (this.hasProgressTarget) gsap.set(this.progressTarget, { scaleY: self.progress })
      }
    })

    return () => this.reset()
  }

  stepsMobile() {
    const count = this.panelTargets.length
    this.element.classList.add("is-animated")
    this.current = -1
    this.activate(0, { immediate: true })

    let timer = null
    let fillTween = null
    const schedule = () => {
      timer?.kill()
      fillTween?.kill()
      const segment = this.segmentTargets[this.current]
      if (segment) fillTween = gsap.fromTo(segment, { scaleX: 0 }, { scaleX: 1, duration: this.cycleValue, ease: "none" })
      timer = gsap.delayedCall(this.cycleValue, () => {
        this.activate((this.current + 1) % count, {})
        schedule()
      })
    }

    let started = false
    const stop = observeVisibility(this.element, (visible) => {
      if (visible && !started) {
        started = true
        this.playPanel(this.current)
        schedule()
      } else if (started) {
        visible ? (timer?.resume(), fillTween?.resume()) : (timer?.pause(), fillTween?.pause())
      }
    }, { rootMargin: "0px", threshold: 0.35 })

    return () => {
      stop()
      timer?.kill()
      fillTween?.kill()
      this.reset()
    }
  }

  activate(index, { desktop = false, immediate = false }) {
    if (index === this.current) return
    const previous = this.current
    this.current = index
    this.element.dataset.activeStep = index

    this.stepTargets.forEach((step, i) => step.classList.toggle("is-active", i === index))
    this.segmentTargets.forEach((segment, i) => {
      if (i !== index) gsap.set(segment, { scaleX: i < index ? 1 : 0 })
    })

    const nextPanel = this.panelTargets[index]
    const prevPanel = this.panelTargets[previous]

    if (immediate) {
      this.panelTargets.forEach((panel, i) => gsap.set(panel, { autoAlpha: i === index ? 1 : 0 }))
      if (desktop) this.stepTargets.forEach((step, i) => gsap.set(step, { opacity: i === index ? 1 : 0 }))
      return
    }

    if (prevPanel) {
      gsap.to(prevPanel, { autoAlpha: 0, scale: 0.985, duration: 0.4, ease: "power2.out", overwrite: true })
      this.dispatchTo(prevPanel, "demo:pause")
    }
    gsap.fromTo(nextPanel,
      { autoAlpha: 0, scale: 0.985 },
      { autoAlpha: 1, scale: 1, duration: 0.8, delay: 0.12, ease: "expo.out", overwrite: true })
    this.playPanel(index)

    if (desktop) {
      const prevStep = this.stepTargets[previous]
      const nextStep = this.stepTargets[index]
      const direction = index > previous ? 1 : -1
      if (prevStep) gsap.to(prevStep, { opacity: 0, y: -24 * direction, duration: 0.45, ease: "power2.out", overwrite: true })
      if (nextStep) gsap.fromTo(nextStep, { opacity: 0, y: 24 * direction }, { opacity: 1, y: 0, duration: 0.8, delay: 0.1, ease: "expo.out", overwrite: true })
    }
  }

  fill(position) {
    this.segmentTargets.forEach((segment, i) => {
      gsap.set(segment, { scaleX: Math.min(1, Math.max(0, position - i)) })
    })
  }

  playPanel(index) {
    const panel = this.panelTargets[index]
    if (panel) this.dispatchTo(panel, "demo:play")
  }

  dispatchTo(panel, name) {
    panel.querySelectorAll("[data-controller~='demo']").forEach((el) => {
      el.dispatchEvent(new CustomEvent(name))
    })
  }

  reset() {
    const animated = [ ...this.panelTargets, ...this.stepTargets, ...this.segmentTargets, ...this.progressTargets ]
    gsap.killTweensOf(animated)
    gsap.set(animated, { clearProps: "opacity,visibility,transform" })
    this.stepTargets.forEach((step) => step.classList.remove("is-active"))
    this.element.classList.remove("is-animated", "is-pinned")
    this.element.dataset.activeStep = 0
    this.current = -1
  }
}
